/**
 * Doctor Appointment Service
 * Handles all doctor appointment-related API calls
 */

import axios from '../api/axios';
import { API_ENDPOINTS } from './api.config';

class DoctorService {
  /**
   * Get list of doctors
   * @param {Object} params - Query parameters
   * @param {string} params.search - Search query (optional)
   * @param {string} params.specialization - Filter by specialization (optional)
   * @returns {Promise<Object>} Response with doctors array
   */
  async getDoctors(params = {}) {
    try {
      const queryParams = new URLSearchParams();

      if (params.search) queryParams.append('search', params.search);
      if (params.specialization) queryParams.append('specialization', params.specialization);

      const response = await axios.get(
        `${API_ENDPOINTS.DOCTORS.LIST}?${queryParams}`
      );
      return response.data;
    } catch (error) {
      throw this.handleError(error);
    }
  }

  /**
   * Get available time slots for a doctor
   * @param {number} doctorId - Doctor ID
   * @param {string} date - Date in YYYY-MM-DD format (optional, defaults to today)
   * @returns {Promise<Object>} Response with available slots
   */
  async getDoctorSlots(doctorId, date = null) {
    try {
      const queryParams = new URLSearchParams();
      if (date) queryParams.append('date', date);

      const response = await axios.get(
        `${API_ENDPOINTS.DOCTORS.SLOTS(doctorId)}?${queryParams}`
      );
      return response.data;
    } catch (error) {
      throw this.handleError(error);
    }
  }

  /**
   * Book doctor appointment
   * @param {Object} bookingData - Booking data
   * @param {number} bookingData.doctor_id - Doctor ID (required)
   * @param {string} bookingData.scheduled_time - ISO datetime "2025-01-20T11:00:00Z" (required)
   * @param {string} bookingData.message - Patient message/symptoms (optional)
   * @returns {Promise<Object>} Response with appointment details
   */
  async bookAppointment(bookingData) {
    try {
      const response = await axios.post(API_ENDPOINTS.DOCTORS.BOOK, {
        doctor: bookingData.doctor_id,
        scheduled_time: bookingData.scheduled_time,
        message: bookingData.message,
      });
      return response.data;
    } catch (error) {
      throw this.handleError(error);
    }
  }

  /**
   * Book normal (queue-based) hospital visit
   * @param {Object} bookingData - Booking data
   * @param {number} bookingData.doctor_id - Doctor ID (required)
   * @param {string} bookingData.visit_date - Visit date in YYYY-MM-DD format (required)
   * @param {string} bookingData.message - Message/reason for visit (optional)
   * @returns {Promise<Object>} Response with appointment details including appointment_id
   */
  async bookNormalAppointment(bookingData) {
    try {
      const payload = {
        doctor_id: bookingData.doctor_id,
        visit_date: bookingData.visit_date,
      };
      if (bookingData.message) payload.message = bookingData.message;
      if (bookingData.patient_address) payload.patient_address = bookingData.patient_address;
      if (bookingData.patient_gender) payload.patient_gender = bookingData.patient_gender;
      if (bookingData.care_of) payload.care_of = bookingData.care_of;
      const response = await axios.post(API_ENDPOINTS.DOCTORS.BOOK_NORMAL, payload);
      return response.data;
    } catch (error) {
      throw this.handleError(error);
    }
  }

  /**
   * Book priority (time slot) hospital visit
   * @param {Object} bookingData - Booking data
   * @param {number} bookingData.doctor_id - Doctor ID (required)
   * @param {string} bookingData.scheduled_time - ISO datetime "2025-01-20T11:00:00+05:30" (required)
   * @param {string} bookingData.message - Message/reason for visit (optional)
   * @returns {Promise<Object>} Response with appointment details including appointment_id
   */
  async bookPriorityAppointment(bookingData) {
    try {
      const payload = {
        doctor_id: bookingData.doctor_id,
        scheduled_time: bookingData.scheduled_time,
      };
      if (bookingData.message) payload.message = bookingData.message;
      if (bookingData.patient_address) payload.patient_address = bookingData.patient_address;
      if (bookingData.patient_gender) payload.patient_gender = bookingData.patient_gender;
      if (bookingData.care_of) payload.care_of = bookingData.care_of;
      const response = await axios.post(API_ENDPOINTS.DOCTORS.BOOK_PRIORITY, payload);
      return response.data;
    } catch (error) {
      throw this.handleError(error);
    }
  }

  /**
   * Convert time string to ISO datetime
   * @param {string} date - Date string YYYY-MM-DD
   * @param {string} time - Time string "11:00 AM"
   * @returns {string} ISO datetime string
   */
  convertToISODateTime(date, time) {
    const dateTime = new Date(`${date} ${time}`);
    return dateTime.toISOString();
  }

  /**
   * Check if slot is within booking rules
   * @param {string} slotDateTime - Slot datetime ISO string
   * @returns {Object} Validation result
   */
  validateSlotTime(slotDateTime) {
    const now = new Date();
    const slotDate = new Date(slotDateTime);
    const diffMs = slotDate - now;
    const diffHours = diffMs / (1000 * 60 * 60);

    // Check if slot is in the past
    if (diffMs < 0) {
      return {
        valid: false,
        message: 'Selected slot is in the past',
      };
    }

    // Allow booking for any future date (no maximum advance booking limit)
    return {
      valid: true,
      message: 'Slot is available',
    };
  }

  /**
   * Get user's doctor appointments
   * @returns {Promise<Object>} Response with appointments array
   */
  async getAppointments() {
    try {
      const response = await axios.get(API_ENDPOINTS.DOCTORS.APPOINTMENTS);
      return response.data;
    } catch (error) {
      throw this.handleError(error);
    }
  }

  /**
   * Reschedule appointment
   * @param {number} appointmentId - Appointment ID
   * @param {string} newDate - New date YYYY-MM-DD
   * @param {string} newTime - New time HH:MM
   * @returns {Promise<Object>} Response with updated appointment
   */
  async rescheduleAppointment(appointmentId, newDate, newTime) {
    try {
      const response = await axios.post(
        API_ENDPOINTS.DOCTORS.RESCHEDULE(appointmentId),
        { new_date: newDate, new_time: newTime }
      );
      return response.data;
    } catch (error) {
      throw this.handleError(error);
    }
  }

  /**
   * Cancel appointment (note: API policy does not allow cancellation)
   * @param {number} appointmentId - Appointment ID
   * @returns {Promise<Object>} Response (will return error per policy)
   */
  async cancelAppointment(appointmentId) {
    try {
      const response = await axios.post(
        API_ENDPOINTS.DOCTORS.CANCEL(appointmentId)
      );
      return response.data;
    } catch (error) {
      throw this.handleError(error);
    }
  }

  /**
   * Get appointment history
   * @param {Object} params - Query parameters
   * @param {number} params.page - Page number
   * @param {number} params.limit - Items per page
   * @returns {Promise<Object>} Response with history and pagination
   */
  async getHistory(params = {}) {
    try {
      const queryParams = new URLSearchParams();
      if (params.page) queryParams.append('page', params.page);
      if (params.limit) queryParams.append('limit', params.limit);

      const response = await axios.get(
        `${API_ENDPOINTS.DOCTORS.HISTORY}?${queryParams}`
      );
      return response.data;
    } catch (error) {
      throw this.handleError(error);
    }
  }

  /**
   * Generate invoice for appointment
   * @param {number} appointmentId - Appointment ID
   * @returns {Promise<Object>} Response with invoice URL
   */
  async generateInvoice(appointmentId) {
    try {
      const response = await axios.post(
        API_ENDPOINTS.DOCTORS.GENERATE_INVOICE(appointmentId)
      );
      return response.data;
    } catch (error) {
      throw this.handleError(error);
    }
  }

  /**
   * Download invoice for appointment
   * @param {number} appointmentId - Appointment ID
   * @returns {Promise<Blob>} PDF file blob
   */
  async downloadInvoice(appointmentId) {
    try {
      const response = await axios.get(
        API_ENDPOINTS.DOCTORS.DOWNLOAD_INVOICE(appointmentId),
        { responseType: 'blob' }
      );
      return response.data;
    } catch (error) {
      throw this.handleError(error);
    }
  }

  /**
   * Download appointment PDF
   * @param {number} appointmentId - Appointment ID
   * @returns {Promise<Blob>} PDF file blob
   */
  async downloadAppointmentPDF(appointmentId) {
    try {
      const response = await axios.get(
        API_ENDPOINTS.DOCTORS.DOWNLOAD_PDF(appointmentId),
        { responseType: 'blob' }
      );
      return response.data;
    } catch (error) {
      throw this.handleError(error);
    }
  }

  /**
   * Handle API errors
   * @param {Error} error - Error object
   * @returns {Error} Formatted error
   */
  handleError(error) {
    if (error.response) {
      const errorMessage = error.response.data.error || error.response.data.message || 'Something went wrong';
      return new Error(errorMessage);
    } else if (error.request) {
      return new Error('Network error. Please check your connection.');
    } else {
      return new Error('An error occurred. Please try again.');
    }
  }
}

export default new DoctorService();
